import { useState, useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// FIN-5 — CategoriaModal (Add | Edit).

// Set sem duplicatas (validado em testes — React reclama de keys
// repetidas quando emoji aparece 2× no array).
const EMOJI_GRID = [
  "🏷️", "🏠", "🍴", "🚗", "💊", "🎬", "📚", "💼", "📈", "👕",
  "💻", "✈️", "🎁", "🔁", "📦", "🎵", "🛒", "💡", "💄", "🐶",
  "🐱", "🍔", "🍕", "☕", "🍷", "🎮", "🎨", "🏋️", "⚽", "🏖️",
  "🎉", "💳", "📱", "🩺", "🏥", "🚿", "🧹", "🌳", "📷", "🎭",
  "📺", "🚌", "🚲", "🛴", "🍦", "🥗", "🍣", "🥑", "🥕", "🍌",
];

const COLOR_PALETTE = [
  "#FF4D6D", "#06B6D4", "#22C55E", "#A855F7", "#F59E0B",
  "#EC4899", "#10B981", "#3B82F6", "#F97316", "#8B5CF6",
  "#7A5CFF", "#FFC857",
];

function ValidationMsg({ children }) {
  return <p className="text-micro text-status-error mt-1" role="alert">{children}</p>;
}

export default function CategoriaModal({
  open,
  onOpenChange,
  category,             // null = create mode
  onCreate,             // (payload) => Promise
  onUpdate,             // (id, patch) => Promise
  knownNames,           // Set<string> (lowercase) pra unicidade client-side
}) {
  const isEditing = Boolean(category);

  const [name, setName] = useState("");
  const [icon, setIcon] = useState("🏷️");
  const [color, setColor] = useState("#7A5CFF");
  const [customHex, setCustomHex] = useState("");
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (open) {
      setName(category?.name || "");
      setIcon(category?.icon || "🏷️");
      setColor(category?.color || "#7A5CFF");
      setCustomHex("");
      setSaving(false);
      setTouched({});
    }
  }, [open, category]);

  const errors = useMemo(() => {
    const out = {};
    const nameTrim = (name || "").trim();
    if (!nameTrim) out.name = "Informe um nome";
    else if (nameTrim.length > 50) out.name = "Máx 50 caracteres";
    else if (knownNames) {
      const lower = nameTrim.toLowerCase();
      const collision = knownNames.has(lower);
      const sameAsExisting = isEditing && (category?.name || "").trim().toLowerCase() === lower;
      if (collision && !sameAsExisting) out.name = "Já existe categoria com esse nome";
    }
    if (customHex && !/^#[0-9A-Fa-f]{6}$/.test(customHex)) {
      out.customHex = "Formato hex: #RRGGBB";
    }
    return out;
  }, [name, customHex, knownNames, isEditing, category]);

  const hasErrors = Object.keys(errors).length > 0;
  const showErr = (k) => touched[k] && errors[k];

  const handleApplyCustom = () => {
    if (/^#[0-9A-Fa-f]{6}$/.test(customHex)) {
      setColor(customHex.toUpperCase());
      setCustomHex("");
    } else {
      setTouched(t => ({ ...t, customHex: true }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true });
    if (hasErrors) return;
    setSaving(true);
    try {
      const payload = { name: name.trim(), icon: icon || "🏷️", color };
      if (isEditing) {
        await onUpdate(category.id, payload);
        toast.success("Categoria atualizada");
      } else {
        await onCreate(payload);
        toast.success("Categoria criada");
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Falha ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-surface-overlay border border-subtle max-w-md max-h-[90vh] overflow-y-auto"
        data-testid="cat-modal"
      >
        <DialogHeader>
          <DialogTitle data-testid="cat-modal-title">
            {isEditing ? `Editar ${category?.name || ""}` : "Nova categoria"}
          </DialogTitle>
          <DialogDescription className="text-foreground-muted text-caption">
            Escolha um ícone e uma cor pra reconhecer rapidamente nas listas.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-4 py-2">
            {/* Nome */}
            <div>
              <label htmlFor="cat-name" className="block text-caption font-medium text-foreground-muted mb-1">
                Nome
              </label>
              <Input
                id="cat-name"
                type="text"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, name: true }))}
                placeholder="Ex: Pet, Saúde, Streaming"
                maxLength={60}
                aria-invalid={Boolean(showErr("name"))}
                data-testid="cat-modal-name"
              />
              {showErr("name") && <ValidationMsg>{errors.name}</ValidationMsg>}
            </div>

            {/* Preview chip */}
            <div className="flex items-center gap-2.5 p-3 rounded-lg bg-surface-card-hover/40 border border-subtle"
                 data-testid="cat-modal-preview">
              <span
                className="w-9 h-9 rounded-lg flex items-center justify-center text-base"
                style={{ backgroundColor: `${color}1A` }}
                aria-hidden="true"
              >
                {icon || "🏷️"}
              </span>
              <span className="text-caption font-semibold text-foreground">
                {name.trim() || "Categoria"}
              </span>
              <span className="ml-auto inline-flex items-center gap-1.5 text-micro text-foreground-muted">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                {color}
              </span>
            </div>

            {/* Emoji picker */}
            <div>
              <label className="block text-caption font-medium text-foreground-muted mb-1.5">
                Ícone
              </label>
              <div className="grid grid-cols-10 gap-1 p-2 rounded-lg bg-surface-card-hover/40 border border-subtle">
                {EMOJI_GRID.map(e => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => setIcon(e)}
                    className={cn(
                      "w-8 h-8 rounded-md flex items-center justify-center text-base transition-colors",
                      icon === e
                        ? "bg-brand-cyan/20 ring-2 ring-brand-cyan/50"
                        : "hover:bg-surface-card-hover",
                    )}
                    aria-label={`Selecionar ícone ${e}`}
                    data-testid={`cat-modal-emoji-${e}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
              <p className="text-micro text-foreground-subtle mt-1.5">
                Você também pode colar um emoji direto no campo Nome.
              </p>
            </div>

            {/* Color picker */}
            <div>
              <label className="block text-caption font-medium text-foreground-muted mb-1.5">
                Cor
              </label>
              <div className="flex flex-wrap gap-1.5">
                {COLOR_PALETTE.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={cn(
                      "w-7 h-7 rounded-full transition-transform",
                      color === c ? "ring-2 ring-offset-2 ring-offset-surface-overlay ring-foreground scale-110" : "hover:scale-105",
                    )}
                    style={{ backgroundColor: c }}
                    aria-label={`Cor ${c}`}
                    data-testid={`cat-modal-color-${c.toLowerCase()}`}
                  />
                ))}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Input
                  type="text"
                  value={customHex}
                  onChange={(e) => setCustomHex(e.target.value)}
                  placeholder="#RRGGBB custom"
                  className="flex-1 max-w-[140px] text-caption"
                  data-testid="cat-modal-color-custom"
                />
                <Button type="button" variant="ghost" size="sm" onClick={handleApplyCustom} disabled={!customHex}>
                  Aplicar
                </Button>
              </div>
              {showErr("customHex") && <ValidationMsg>{errors.customHex}</ValidationMsg>}
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} data-testid="cat-modal-cancel">
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={saving || hasErrors} data-testid="cat-modal-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isEditing ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
